import React, { useEffect } from "react"
import {
  Badge,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getBrandDetails } from "store/actions/brandsAction"
import { saGetBrandDetails } from "store/actions/superAdminAction"
import { clearBrandDetails } from "store/reducers/brandsReducer"
import { formatPaymentMethodLabels } from "pages/AppSettings/paymentMethodConfig"

const BRAND_DETAILS_SCOPE_CONFIG = {
  tenant: {
    permissionCategory: 52,
    permissionId: "brands_1",
    fallbackRoute: "/dashboard",
    action: getBrandDetails,
  },
  superadmin: {
    permissionCategory: 108,
    permissionId: "sa_brands_1",
    fallbackRoute: "/superadmin/dashboard",
    action: saGetBrandDetails,
  },
}

const BrandDetails = ({ scope = "tenant" }) => {
  const config = BRAND_DETAILS_SCOPE_CONFIG[scope] || BRAND_DETAILS_SCOPE_CONFIG.tenant
  const { t } = useTranslation()
  document.title = t("Brand Details ")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const id = new URLSearchParams(useLocation().search).get("id")
  const { loading, brandDetails } = useSelector(state => state.Brands)
  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const canView = isPermissionEnabled(
      userRole,
      config.permissionCategory,
      config.permissionId,
      accountType
    )
    if (!canView) {
      navigate(config.fallbackRoute)
      return
    }
    if (id) {
      dispatch(config.action({ id }))
    }
    return () => {
      dispatch(clearBrandDetails())
    }
  }, [id, userRole, accountType, dispatch, navigate, config])

  if (loading || !brandDetails) {
    return <Spinners />
  }

  const paymentMethods =
    formatPaymentMethodLabels(brandDetails.allowedPaymentMethods, t) || "-"

  const statusValue = (
    <Badge color={brandDetails.status === 1 ? "success" : "secondary"}>
      {brandDetails.status === 1 ? t("Active") : t("Inactive")}
    </Badge>
  )

  const defaultValue = brandDetails.isDefault ? t("Yes") : t("No")

  const rows = [
    [t("Brand Name"), brandDetails.brandName],
    [t("Integration"), brandDetails.integration || "-"],
    [t("Description"), brandDetails.brandDesc || "-"],
    [t("Allowed Payment Methods"), paymentMethods],
    [t("Status"), statusValue],
    [t("Default"), defaultValue],
  ]

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center gap-2">
                  <BackButton />
                  {t("Brand Details")}
                  <Badge color={brandDetails.status === 1 ? "success" : "secondary"}>
                    {brandDetails.status === 1 ? t("Active") : t("Inactive")}
                  </Badge>
                </CardTitle>
                <Table className="table-borderless mb-0 mt-3">
                  <tbody>
                    {rows.map(([label, value]) => (
                      <tr key={label}>
                        <th style={{ width: "220px" }}>{label}</th>
                        <td>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default BrandDetails
